import { UseMutationOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "types/models.ts";

import { invoiceApi } from "./api";

import GetInvoiceResponse = App.Http.Responses.Invoice.GetInvoiceResponse;
import GetInvoicesRequest = App.Http.Requests.Invoice.GetInvoicesRequest;
import GetInvoicesResponse = App.Http.Responses.Invoice.GetInvoicesResponse;
import StoreInvoiceRequest = App.Http.Requests.Invoice.StoreInvoiceRequest;
import UpdateInvoiceRequest = App.Http.Requests.Invoice.UpdateInvoiceRequest;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import ExportResponse = App.Http.Responses.ExportResponse;

// Query to get a list of invoices
export const useGetInvoicesQuery = (request: GetInvoicesRequest, options = {}) => {
    return useQueryApi<GetInvoicesResponse>({
        queryKey: ["getInvoices", request],
        queryFn: () => invoiceApi.index(request),
        ...options,
    });
};

// Query to get a specific invoice
export const useGetInvoiceQuery = (invoiceId: string, request?: unknown, options = {}) => {
    return useQueryApi<GetInvoiceResponse>({
        queryKey: ["getInvoice", invoiceId],
        queryFn: () => invoiceApi.show(invoiceId, request),
        ...options,
    });
};

// Mutation to store a new invoice
export const useStoreInvoiceMutation = (
    options: UseMutationOptions<GetInvoiceResponse, AxiosError<ApiErrorResponse>, StoreInvoiceRequest> = {}
) => {
    return useMutationApi<GetInvoiceResponse, StoreInvoiceRequest>({
        mutationKey: ["storeInvoice"],
        mutationFn: (request) => invoiceApi.store(request),
        ...options,
    });
};

// Mutation to update an existing invoice
export const useUpdateInvoiceMutation = (
    options: UseMutationOptions<
        GetInvoiceResponse,
        AxiosError<ApiErrorResponse>,
        { invoiceId: string; request: UpdateInvoiceRequest }
    > = {}
) => {
    return useMutationApi<GetInvoiceResponse, { invoiceId: string; request: UpdateInvoiceRequest }>({
        mutationKey: ["updateInvoice"],
        mutationFn: ({ invoiceId, request }) => invoiceApi.update(invoiceId, request),
        ...options,
    });
};

// Mutation to destroy an invoice
export const useDestroyInvoiceMutation = (
    options: UseMutationOptions<void, AxiosError<ApiErrorResponse>, string> = {}
) => {
    return useMutationApi<void, string>({
        mutationKey: ["destroyInvoice"],
        mutationFn: (invoiceId: string) => invoiceApi.destroy(invoiceId),
        ...options,
    });
};

export const useExportInvoicesMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["exportInvoices"],
        mutationFn: (request) => invoiceApi.export(request),
        ...options,
    });
};
